import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BellRing } from 'lucide-react';
import {
  CustomerReminderType,
  type AuthenticatedBranch,
  type CustomerReminderView,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useReminderActionMutation,
  useRemindersQuery,
  type ReminderListParams,
} from '../api/crm.api';

const TONE: Record<string, 'neutral' | 'success' | 'warning'> = {
  OPEN: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};

/**
 * Follow-ups somebody meant to make.
 *
 * Deliberately not a task platform: no priorities, no assignees beyond the one the backend records,
 * no recurrence, and **no delete** — a reminder is completed or cancelled, and either way it stays,
 * because "who decided to stop chasing this, and when" is the useful question later.
 */
export function RemindersPanel({
  canManage,
  branches,
}: {
  canManage: boolean;
  branches: AuthenticatedBranch[];
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ReminderListParams['status']>('OPEN');
  const [type, setType] = useState<CustomerReminderType | ''>('');
  const [page, setPage] = useState(1);
  const [failure, setFailure] = useState<string | null>(null);

  const params: ReminderListParams = { page, pageSize: 20, status, type };
  const { data, isPending, isError, error, refetch } = useRemindersQuery(params);
  const act = useReminderActionMutation();

  const run = (id: string, action: 'complete' | 'cancel') => {
    setFailure(null);
    act.mutate(
      { id, action },
      {
        onError: (mutationError) =>
          setFailure(apiErrorMessage(mutationError) ?? t('crm.reminders.actionFailed')),
      },
    );
  };

  const branchName = (id: string): string =>
    branches.find((branch) => branch.id === id)?.name ?? id;

  const columns: Column<CustomerReminderView>[] = [
    {
      key: 'customer',
      header: t('crm.fields.customer'),
      primary: true,
      render: (row) => (
        <Link className="text-brand underline" to={`/customers/${row.customerId}`}>
          {t('crm.reminders.openCustomer')}
        </Link>
      ),
    },
    {
      key: 'dueDate',
      header: t('crm.fields.dueDate'),
      render: (row) => <span dir="ltr">{row.dueDate}</span>,
    },
    {
      key: 'type',
      header: t('crm.fields.type'),
      render: (row) => t(`crm.reminderType.${row.type}`),
    },
    {
      key: 'branch',
      header: t('crm.fields.branch'),
      hideOnCard: true,
      render: (row) => <span dir="auto">{branchName(row.branchId)}</span>,
    },
    {
      key: 'status',
      header: t('crm.fields.status'),
      render: (row) => (
        <Badge tone={TONE[row.status] ?? 'neutral'}>{t(`crm.reminderStatus.${row.status}`)}</Badge>
      ),
    },
    {
      key: 'note',
      header: t('crm.fields.note'),
      hideOnCard: true,
      render: (row) => <span dir="auto">{row.note ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (row) =>
        canManage && row.status === 'OPEN' ? (
          <span className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => run(row.id, 'complete')}>
              {t('crm.reminders.complete')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => run(row.id, 'cancel')}>
              {t('crm.reminders.cancel')}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <Card>
      <CardHeader
        title={t('crm.reminders.title')}
        subtitle={
          data ? t('crm.reminders.count', { count: data.pagination.totalItems }) : undefined
        }
      />

      {failure ? (
        <div className="px-4 pt-3">
          <Alert tone="error">{failure}</Alert>
        </div>
      ) : null}

      <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2">
        <Select
          aria-label={t('crm.filters.status')}
          options={[
            { value: '', label: t('crm.filters.allStatuses') },
            ...['OPEN', 'COMPLETED', 'CANCELLED'].map((value) => ({
              value,
              label: t(`crm.reminderStatus.${value}`),
            })),
          ]}
          value={status ?? ''}
          onChange={(event) => {
            setStatus(event.target.value as ReminderListParams['status']);
            setPage(1);
          }}
        />
        <Select
          aria-label={t('crm.filters.type')}
          options={[
            { value: '', label: t('crm.filters.allTypes') },
            ...Object.values(CustomerReminderType).map((value) => ({
              value,
              label: t(`crm.reminderType.${value}`),
            })),
          ]}
          value={type}
          onChange={(event) => {
            setType(event.target.value as CustomerReminderType | '');
            setPage(1);
          }}
        />
      </div>

      <DataTable
        caption={t('crm.reminders.tableCaption')}
        columns={columns}
        rows={data?.items}
        rowKey={(row) => row.id}
        isLoading={isPending}
        isError={isError}
        errorMessage={apiErrorMessage(error) ?? t('crm.reminders.loadError')}
        onRetry={() => void refetch()}
        emptyIcon={BellRing}
        emptyTitle={t('crm.reminders.empty')}
        emptyDescription={t('crm.reminders.emptyHint')}
      />

      {data ? <Pagination pagination={data.pagination} onPageChange={setPage} /> : null}
    </Card>
  );
}
